import type {
  AlgoliaInsightsConfig,
  AlgoliaInsightsContextType,
  ObjectData,
} from '@maestro/algolia-insights/types'
import type React from 'react'

import chunk from 'lodash.chunk'

import {
  addedToCartObjectIDs,
  addedToCartObjectIDsAfterSearch,
  clickedFilters,
  clickedObjectIDs,
  clickedObjectIDsAfterSearch,
  convertedObjectIDs,
  convertedObjectIDsAfterSearch,
  purchasedObjectIDsAfterSearch,
  viewedObjectIDs,
} from '@maestro/algolia-insights/algolia-insights-client'
import { userTokenCookieService } from '@maestro/algolia-insights/user-token-cookie.service'
import { getUserToken } from '@maestro/algolia-insights/user-token-manager'
import { generateUUID } from '@maestro/algolia-insights/utils'
import { getCartIdFromStorage } from '@maestro/commerce-blocks/session/cart-store'
import { divideAmount } from '@maestro/commerce-blocks/utils/price.utils'
import {
  getProductQueryId,
  getProductQueryIdFromCart,
} from '@maestro/core/event/queryid-tracker.utils'
import { createLogger } from '@maestro/core/logger'
import {
  CookieConsent,
  type EventPayloadType,
  EventType,
  type Item,
} from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'

const log = createLogger('maestro:algolia-insights')

function debugLog(isDebug: boolean | undefined, message: string): void {
  if (isDebug) {
    log.debug(message)
  }
}

/**
 * Get queryID for a product from cart, event data, or products map
 */
function getQueryIDForProduct(
  productId: string,
  eventQueryID?: string
): string {
  if (eventQueryID) {
    return eventQueryID
  }

  const cartData = getCartIdFromStorage()
  if (cartData?.cartId) {
    const cartQueryID = getProductQueryIdFromCart(cartData.cartId, productId)
    if (cartQueryID) {
      return cartQueryID
    }
  }

  return getProductQueryId(productId)
}

/**
 * Create object data array from items
 */
function createObjectData(items: Item[]): ObjectData[] {
  return items.map((item: Item) => {
    const priceAmount = item.product.price?.amount || 0
    const { integerPrice, decimalPrice } = divideAmount(priceAmount)

    const objectData: ObjectData = {
      price: parseFloat(`${integerPrice}.${decimalPrice}`),
      quantity: item.quantity || 1,
    }

    if (item.queryID) {
      objectData.queryID = item.queryID
    }

    return objectData
  })
}

/**
 * Calculate total value from object data
 */
function calculateTotalValue(objectData: ObjectData[]): number {
  return objectData.reduce((sum, obj) => sum + obj.price * obj.quantity, 0)
}

/**
 * Resolve Algolia consent from cookie consents based on config.
 * Returns true only when every configured consent is granted (AND).
 * Returns undefined when no configured consents or any are missing from
 * the payload (treated by handlers as "no decision yet").
 */
function getAlgoliaConsent(
  consents: CookieConsent = {},
  config: AlgoliaInsightsConfig
): boolean | undefined {
  const names = config.cookieConsentNames
  if (!names.length) {
    return undefined
  }
  const values = names.map((n) => consents[n as keyof CookieConsent])
  if (values.some((v) => v === undefined)) {
    return undefined
  }
  return values.every(Boolean)
}

/**
 * Send purchase event for all items, with queryID per item in objectData where available
 */
function sendPurchaseEventsWithQueryID(
  items: Item[],
  index: string,
  userToken: string,
  authenticatedUserToken: string | undefined,
  currency: string,
  isDebug: boolean
): void {
  if (!items.length) {
    return
  }

  const objectIDs = items.map((item: Item) => item.product.id)
  const objectData = createObjectData(items)
  const totalValue = calculateTotalValue(objectData)
  const { integerPrice: integerValue, decimalPrice: decimalValue } =
    divideAmount(totalValue)
  const value = parseFloat(`${integerValue}.${decimalValue}`)

  purchasedObjectIDsAfterSearch(
    {
      index,
      userToken,
      authenticatedUserToken,
      objectIDs,
      objectData,
      value,
      currency,
      eventName: 'Order Completed',
    },
    isDebug
  )
}

/**
 * Handle PRODUCT_LIST_VIEWED event
 */
export function handleProductListViewed(
  data: EventPayloadType[EventType.PRODUCT_LIST_VIEWED],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  const index = data.searchServiceIndex
  if (!index) {
    debugLog(
      config.isDebug,
      '[Algolia Insights] Skipping PRODUCT_LIST_VIEWED - no index provided'
    )
    return
  }

  const tokens = getUserToken(
    data.authenticatedUserToken,
    contextRef.current?.isConsentGranted || false
  )
  const objectIDs = data.items.map((item: Item) => item.product.id)

  if (!objectIDs.length) {
    return
  }

  // Send objectIDs in batches of 20
  const objectIDsChunks = chunk(objectIDs, 20)
  objectIDsChunks.forEach((objectIDsChunk: string[]) => {
    viewedObjectIDs(
      {
        index,
        userToken: tokens.userToken,
        authenticatedUserToken: tokens.authenticatedUserToken,
        objectIDs: objectIDsChunk,
        eventName: 'Product List Viewed',
      },
      config.isDebug
    )
  })
}

/**
 * Handle PRODUCT_CLICKED event
 */
export function handleProductClicked(
  data: EventPayloadType[EventType.PRODUCT_CLICKED],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  const index = data.searchServiceIndex
  if (!index) {
    debugLog(
      config.isDebug,
      '[Algolia Insights] Skipping PRODUCT_CLICKED - no index provided'
    )
    return
  }

  const tokens = getUserToken(
    data.authenticatedUserToken,
    contextRef.current?.isConsentGranted || false
  )
  const objectID = data.item.product.id
  const queryID = data.queryID

  if (queryID) {
    clickedObjectIDsAfterSearch(
      {
        index,
        userToken: tokens.userToken,
        authenticatedUserToken: tokens.authenticatedUserToken,
        objectIDs: [objectID],
        positions: [(data.item.position || 0) + 1],
        queryID,
        eventName: 'Product Clicked',
      },
      config.isDebug
    )
  } else {
    clickedObjectIDs(
      {
        index,
        userToken: tokens.userToken,
        authenticatedUserToken: tokens.authenticatedUserToken,
        objectIDs: [objectID],
        eventName: 'Product Clicked',
      },
      config.isDebug
    )
  }
}

/**
 * Handle PRODUCT_VIEWED event
 */
export function handleProductViewed(
  data: EventPayloadType[EventType.PRODUCT_VIEWED],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  const index = data.searchServiceIndex
  if (!index) {
    debugLog(
      config.isDebug,
      '[Algolia Insights] Skipping PRODUCT_VIEWED - no index provided'
    )
    return
  }

  const tokens = getUserToken(
    data.authenticatedUserToken,
    contextRef.current?.isConsentGranted || false
  )
  const objectID = data.item.product.id

  viewedObjectIDs(
    {
      index,
      userToken: tokens.userToken,
      authenticatedUserToken: tokens.authenticatedUserToken,
      objectIDs: [objectID],
      eventName: 'Product Viewed',
    },
    config.isDebug
  )
}

/**
 * Handle PRODUCT_ADDED_TO_CART event
 *
 * Logic based on triggeredFrom field:
 * - PDP from PLP: convertedObjectIDsAfterSearch
 * - PDP from search: addedToCartObjectIDsAfterSearch
 * - PDP (no context): addedToCartObjectIDs
 * - search/PLP direct add: addedToCartObjectIDsAfterSearch
 * - cart/wishlist/carousel/recommendations: addedToCartObjectIDs
 */
export function handleProductAddedToCart(
  data: EventPayloadType[EventType.PRODUCT_ADDED_TO_CART],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  const index = data.searchServiceIndex
  if (!index) {
    debugLog(
      config.isDebug,
      '[Algolia Insights] Skipping PRODUCT_ADDED_TO_CART - no index provided'
    )
    return
  }

  const tokens = getUserToken(
    data.authenticatedUserToken,
    contextRef.current?.isConsentGranted || false
  )
  const objectID = data.item.product.id
  const queryID = getQueryIDForProduct(objectID, data.queryID)

  // Prepare object data with price and quantity
  const priceAmount = data.item.product.price?.amount || 0
  const { integerPrice, decimalPrice } = divideAmount(priceAmount)
  const objectData = [
    {
      price: parseFloat(`${integerPrice}.${decimalPrice}`),
      quantity: data.item.quantity || 1,
    },
  ]
  const totalValue = objectData[0].price * objectData[0].quantity
  const { integerPrice: integerValue, decimalPrice: decimalValue } =
    divideAmount(totalValue)
  const value = parseFloat(`${integerValue}.${decimalValue}`)
  const { currencyIso } = theme().priceConfig
  const currency = data.item.product.price?.currency || currencyIso

  const triggeredFrom = data.triggeredFrom
  const listType = data.listType

  // Determine which Algolia event to fire based on triggeredFrom and context
  if (triggeredFrom === 'PDP') {
    // PDP add to cart
    if (queryID && listType === 'category') {
      // User navigated to PDP from PLP (category listing)
      convertedObjectIDsAfterSearch(
        {
          index,
          userToken: tokens.userToken,
          authenticatedUserToken: tokens.authenticatedUserToken,
          objectIDs: [objectID],
          queryID,
          eventName: 'Product Added to Cart',
        },
        config.isDebug
      )
    } else if (queryID && listType === 'search') {
      // User navigated to PDP from search
      addedToCartObjectIDsAfterSearch(
        {
          index,
          userToken: tokens.userToken,
          authenticatedUserToken: tokens.authenticatedUserToken,
          objectIDs: [objectID],
          queryID,
          objectData,
          value,
          currency,
          eventName: 'Product Added to Cart',
        },
        config.isDebug
      )
    } else if (queryID) {
      // Has queryID but listType is unclear, default to addedToCartObjectIDsAfterSearch
      addedToCartObjectIDsAfterSearch(
        {
          index,
          userToken: tokens.userToken,
          authenticatedUserToken: tokens.authenticatedUserToken,
          objectIDs: [objectID],
          queryID,
          objectData,
          value,
          currency,
          eventName: 'Product Added to Cart',
        },
        config.isDebug
      )
    } else {
      // No search context (direct navigation to PDP)
      addedToCartObjectIDs(
        {
          index,
          userToken: tokens.userToken,
          authenticatedUserToken: tokens.authenticatedUserToken,
          objectIDs: [objectID],
          objectData,
          value,
          currency,
          eventName: 'Product Added to Cart',
        },
        config.isDebug
      )
    }
  } else if (triggeredFrom === 'search' || triggeredFrom === 'PLP') {
    // Direct add to cart from search or PLP
    if (queryID) {
      addedToCartObjectIDsAfterSearch(
        {
          index,
          userToken: tokens.userToken,
          authenticatedUserToken: tokens.authenticatedUserToken,
          objectIDs: [objectID],
          queryID,
          objectData,
          value,
          currency,
          eventName: 'Product Added to Cart',
        },
        config.isDebug
      )
    } else {
      // Fallback if queryID is missing (shouldn't normally happen)
      addedToCartObjectIDs(
        {
          index,
          userToken: tokens.userToken,
          authenticatedUserToken: tokens.authenticatedUserToken,
          objectIDs: [objectID],
          objectData,
          value,
          currency,
          eventName: 'Product Added to Cart',
        },
        config.isDebug
      )
    }
  } else {
    // cart page quantity increment, wishlist, carousels, recommendations
    addedToCartObjectIDs(
      {
        index,
        userToken: tokens.userToken,
        authenticatedUserToken: tokens.authenticatedUserToken,
        objectIDs: [objectID],
        objectData,
        value,
        currency,
        eventName: 'Product Added to Cart',
      },
      config.isDebug
    )
  }
}

/**
 * Handle PRODUCT_ADDED_TO_WISHLIST event
 * Uses convertedObjectIDs with custom eventName for wishlist actions
 */
export function handleProductAddedToWishlist(
  data: EventPayloadType[EventType.PRODUCT_ADDED_TO_WISHLIST],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  const index = data.searchServiceIndex
  if (!index) {
    debugLog(
      config.isDebug,
      '[Algolia Insights] Skipping PRODUCT_ADDED_TO_WISHLIST - no index provided'
    )
    return
  }

  const tokens = getUserToken(
    data.authenticatedUserToken,
    contextRef.current?.isConsentGranted || false
  )
  const objectID = data.item.product.id
  const queryID = getQueryIDForProduct(objectID, data.queryID)

  if (queryID) {
    convertedObjectIDsAfterSearch(
      {
        index,
        userToken: tokens.userToken,
        authenticatedUserToken: tokens.authenticatedUserToken,
        objectIDs: [objectID],
        queryID,
        eventName: 'Product Added to Wishlist',
      },
      config.isDebug
    )
  } else {
    convertedObjectIDs(
      {
        index,
        userToken: tokens.userToken,
        authenticatedUserToken: tokens.authenticatedUserToken,
        objectIDs: [objectID],
        eventName: 'Product Added to Wishlist',
      },
      config.isDebug
    )
  }
}

/**
 * Handle PRODUCT_SUBSCRIBED_TO_OUT_OF_STOCK event
 * Uses convertedObjectIDs with custom eventName for OOS subscriptions
 */
export function handleProductSubscribedToOutOfStock(
  data: EventPayloadType[EventType.PRODUCT_SUBSCRIBED_TO_OUT_OF_STOCK],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  const index = data.searchServiceIndex
  if (!index) {
    debugLog(
      config.isDebug,
      '[Algolia Insights] Skipping PRODUCT_SUBSCRIBED_TO_OUT_OF_STOCK - no index provided'
    )
    return
  }

  const tokens = getUserToken(
    data.authenticatedUserToken,
    contextRef.current?.isConsentGranted || false
  )
  const objectID = data.item.product.id
  const queryID = getQueryIDForProduct(objectID, data.queryID)

  if (queryID) {
    convertedObjectIDsAfterSearch(
      {
        index,
        userToken: tokens.userToken,
        authenticatedUserToken: tokens.authenticatedUserToken,
        objectIDs: [objectID],
        queryID,
        eventName: 'Product Subscribed to Out of Stock',
      },
      config.isDebug
    )
  } else {
    convertedObjectIDs(
      {
        index,
        userToken: tokens.userToken,
        authenticatedUserToken: tokens.authenticatedUserToken,
        objectIDs: [objectID],
        eventName: 'Product Subscribed to Out of Stock',
      },
      config.isDebug
    )
  }
}

/**
 * Handle PRODUCTS_FILTERED event
 * Tracks when users apply filters to product listings
 */
export function handleProductsFiltered(
  data: EventPayloadType[EventType.PRODUCTS_FILTERED],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  const index = data.searchServiceIndex
  if (!index) {
    debugLog(
      config.isDebug,
      '[Algolia Insights] Skipping PRODUCTS_FILTERED - no index provided'
    )
    return
  }

  const tokens = getUserToken(
    data.authenticatedUserToken,
    contextRef.current?.isConsentGranted || false
  )

  // Create filter string in Algolia format (e.g., "category:shoes")
  const filterString = `${data.filterName}:${data.filterValue}`

  clickedFilters(
    {
      index,
      userToken: tokens.userToken,
      authenticatedUserToken: tokens.authenticatedUserToken,
      filters: [filterString],
      eventName: 'Products Filtered',
    },
    config.isDebug
  )
}

/**
 * Handle ORDER_COMPLETED event
 */
export function handleOrderCompleted(
  data: EventPayloadType[EventType.ORDER_COMPLETED],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  const index = data.searchServiceIndex
  if (!index) {
    debugLog(
      config.isDebug,
      '[Algolia Insights] Skipping ORDER_COMPLETED - no index provided'
    )
    return
  }

  const tokens = getUserToken(
    data.authenticatedUserToken,
    contextRef.current?.isConsentGranted || false
  )

  sendPurchaseEventsWithQueryID(
    data.items,
    index,
    tokens.userToken,
    tokens.authenticatedUserToken,
    data.currency,
    config.isDebug || false
  )
}

/**
 * Handle COLLECT_AT_STORE_ORDER_COMPLETED event
 */
export function handleCollectAtStoreOrderCompleted(
  data: EventPayloadType[EventType.COLLECT_AT_STORE_ORDER_COMPLETED],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  const index = data.searchServiceIndex
  if (!index) {
    debugLog(
      config.isDebug,
      '[Algolia Insights] Skipping COLLECT_AT_STORE_ORDER_COMPLETED - no index provided'
    )
    return
  }

  const tokens = getUserToken(
    data.authenticatedUserToken,
    contextRef.current?.isConsentGranted || false
  )

  sendPurchaseEventsWithQueryID(
    data.items,
    index,
    tokens.userToken,
    tokens.authenticatedUserToken,
    data.currency,
    config.isDebug || false
  )
}

/**
 * Handle ARTICLE_CLICKED event
 */
export function handleArticleClicked(
  data: EventPayloadType[EventType.ARTICLE_CLICKED],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  const index = data.searchServiceIndex || config.indexArticles
  if (!index) {
    debugLog(
      config.isDebug,
      '[Algolia Insights] Skipping ARTICLE_CLICKED - no index provided'
    )
    return
  }

  const tokens = getUserToken(
    data.authenticatedUserToken,
    contextRef.current?.isConsentGranted || false
  )

  if (data.queryID) {
    clickedObjectIDsAfterSearch(
      {
        index,
        userToken: tokens.userToken,
        authenticatedUserToken: tokens.authenticatedUserToken,
        objectIDs: [data.objectID],
        positions: [(data.position || 0) + 1],
        queryID: data.queryID,
        eventName: 'Article Clicked',
      },
      config.isDebug
    )
  } else {
    clickedObjectIDs(
      {
        index,
        userToken: tokens.userToken,
        authenticatedUserToken: tokens.authenticatedUserToken,
        objectIDs: [data.objectID],
        eventName: 'Article Clicked',
      },
      config.isDebug
    )
  }
}

/**
 * Handle ARTICLE_VIEWED event
 */
export function handleArticleViewed(
  data: EventPayloadType[EventType.ARTICLE_VIEWED],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  const index = data.searchServiceIndex || config.indexArticles
  if (!index) {
    debugLog(
      config.isDebug,
      '[Algolia Insights] Skipping ARTICLE_VIEWED - no index provided'
    )
    return
  }

  const tokens = getUserToken(
    data.authenticatedUserToken,
    contextRef.current?.isConsentGranted || false
  )

  viewedObjectIDs(
    {
      index,
      userToken: tokens.userToken,
      authenticatedUserToken: tokens.authenticatedUserToken,
      objectIDs: [data.objectID],
      eventName: 'Article Viewed',
    },
    config.isDebug
  )
}

/**
 * Handle ARTICLE_LIST_VIEWED event
 */
export function handleArticleListViewed(
  data: EventPayloadType[EventType.ARTICLE_LIST_VIEWED],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  const index = data.searchServiceIndex || config.indexArticles
  if (!index) {
    debugLog(
      config.isDebug,
      '[Algolia Insights] Skipping ARTICLE_LIST_VIEWED - no index provided'
    )
    return
  }

  const tokens = getUserToken(
    data.authenticatedUserToken,
    contextRef.current?.isConsentGranted || false
  )

  if (!data.objectIDs.length) {
    return
  }

  const objectIDsChunks = chunk(data.objectIDs, 20)
  objectIDsChunks.forEach((objectIDsChunk: string[]) => {
    viewedObjectIDs(
      {
        index,
        userToken: tokens.userToken,
        authenticatedUserToken: tokens.authenticatedUserToken,
        objectIDs: objectIDsChunk,
        eventName: 'Article List Viewed',
      },
      config.isDebug
    )
  })
}

export function handleCookieConsentSetEvent(
  payloadData: EventPayloadType[EventType.COOKIE_CONSENT_SET],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  // Skip if dialog is open - we only process this on initial load
  if (contextRef.current.isCookieConsentDialogOpened) {
    return
  }

  const consent = getAlgoliaConsent(payloadData.cookieConsent, config)
  contextRef.current.setIsConsentGranted(!!consent)

  // Ensure cookie exists when consent is granted (handles page reload scenario)
  if (consent) {
    userTokenCookieService.getOrCreate()
  }
}

export function handleCookieConsentDeclinedEvent(
  payloadData: EventPayloadType[EventType.COOKIE_CONSENT_DECLINED],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  // Only process if dialog is open
  if (!contextRef.current.isCookieConsentDialogOpened) {
    return
  }

  const consent = getAlgoliaConsent(payloadData.cookieConsent, config)

  // Only update if consent actually changed
  if (
    consent !== undefined &&
    consent !== contextRef.current.isConsentGranted
  ) {
    contextRef.current.setIsConsentGranted(consent)
    userTokenCookieService.remove()
  }

  contextRef.current.setIsCookieConsentDialogOpened(false)
}

export function handleCookieConsentGrantedEvent(
  payloadData: EventPayloadType[EventType.COOKIE_CONSENT_GRANTED],
  config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  // Only process if dialog is open
  if (!contextRef.current.isCookieConsentDialogOpened) {
    return
  }

  const consent = getAlgoliaConsent(payloadData.cookieConsent, config)

  // Only update if consent actually changed
  if (
    consent !== undefined &&
    consent !== contextRef.current.isConsentGranted
  ) {
    if (consent) {
      const token = generateUUID()
      userTokenCookieService.set(token)
    } else {
      userTokenCookieService.remove()
    }
    contextRef.current.setIsConsentGranted(consent)
  }

  contextRef.current.setIsCookieConsentDialogOpened(false)
}

export function handleCookieConsentDialogOpenedEvent(
  _payloadData: EventPayloadType[EventType.COOKIE_CONSENT_DIALOG_OPENED],
  _config: AlgoliaInsightsConfig,
  contextRef: React.RefObject<AlgoliaInsightsContextType>
): void {
  if (!contextRef.current) {
    return
  }

  contextRef.current.setIsCookieConsentDialogOpened(true)
}
